/**
 * 2열 메뉴 «묶음» 배치 — 결재 · 근태 · 인사 (web 레인 W5 · 2026-10-17)
 *
 * 계획 `docs/design/plan-20261017-web-common-frame.md` F2 · 시안 `shellC.py` appr_tree2 · att_tree2 · hr_tree2.
 * 사용자 결정 D2(서명 · 도장 · 맡은 조직 · 입사 처리 남김) · D3(묶음 이름만 시안대로 · 항목 이름은 그대로).
 *
 * ── 왜 «다시 담기» 인가 ────────────────────────────────────────────────────
 * 항목의 정의(이름 · 설명 · 아이콘 · 주소)와 역할 거르기(`apprMenuFor` · `attMenuFor` · `hrMenuFor`)는
 * 각 `*-menu.ts` 가 계속 맡는다 — 여기서는 **이미 걸러진 묶음을 주소로 새 묶음에 다시 담기만** 한다.
 *   · 그래서 역할마다 보이는 항목은 오늘과 정확히 같다(빠지지도 늘지도 않는다 — 시험이 역할마다 잰다).
 *   · 배치표에 없는 항목은 **원래 묶음 이름으로 끝에** 붙는다 — 새 메뉴를 더했는데 배치표를 잊어도
 *     항목이 사라지지 않는다.
 *   · 비는 묶음은 서지 않는다(직원에게는 «결재 운영» 이 없다).
 */

/** 묶음 하나 — 이름과, 그 묶음에 담을 항목의 열쇠(주소, 또는 창을 여는 항목은 `action:이름`). */
export interface MenuLayoutGroup {
  label: string;
  keys: readonly string[];
}

interface KeyedItem { to?: string; action?: string }
interface Group<I> { label: string; items: I[] }

/** 항목의 열쇠 — 주소가 있으면 주소, 창을 여는 항목은 `action:이름`. */
export function menuKey(item: KeyedItem): string {
  return item.to ?? `action:${item.action ?? ''}`;
}

/**
 * 걸러진 묶음을 배치표대로 다시 담는다.
 * ★ 순수 함수 — 들어온 묶음 · 항목을 고치지 않고 새 배열을 돌려준다.
 */
export function regroup<I extends KeyedItem>(
  groups: readonly Group<I>[],
  layout: readonly MenuLayoutGroup[],
): Group<I>[] {
  const all = groups.flatMap((g) => g.items.map((item) => ({ item, from: g.label })));
  const seen = new Set<string>();
  const unique = all.filter(({ item }) => {
    const k = menuKey(item);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const byKey = new Map(unique.map((u) => [menuKey(u.item), u.item] as const));
  const placed = new Set<string>();

  const laidOut = layout.map((lg) => ({
    label: lg.label,
    items: lg.keys.flatMap((k) => {
      const item = byKey.get(k);
      if (!item || placed.has(k)) return [];
      placed.add(k);
      return [item];
    }),
  }));

  // 배치표에 없는 항목 — 원래 묶음 이름의 묶음 끝에 붙인다(그 이름이 배치표에 있으면 그 묶음, 없으면 맨 끝에 새로)
  const leftovers = unique.filter(({ item }) => !placed.has(menuKey(item)));
  const merged = leftovers.reduce<Group<I>[]>((acc, { item, from }) => {
    const at = acc.findIndex((g) => g.label === from);
    if (at < 0) return [...acc, { label: from, items: [item] }];
    return acc.map((g, i) => (i === at ? { ...g, items: [...g.items, item] } : g));
  }, laidOut);

  return merged.filter((g) => g.items.length > 0);
}

/** 전자결재 — 결재하기(+내 위임) · 결재 운영 · 돈 · 예산 · 개인 설정(서명 · 도장, D2). */
export const APPR_LAYOUT: readonly MenuLayoutGroup[] = [
  { label: '결재하기', keys: ['/approval', '/approval/box', '/approval/expense', '/approval/me/delegates'] },
  {
    label: '결재 운영',
    keys: ['/approval/admin/stats', '/approval/admin/forms', '/approval/admin/boxes', '/approval/admin/lines', '/approval/admin/delegates'],
  },
  { label: '돈 · 예산', keys: ['/approval/admin/codes', '/approval/admin/expense', '/approval/admin/budget'] },
  { label: '개인 설정', keys: ['action:signature'] },
];

/** 근태 — 개인 · 근태 관리 · 연차 · 회사 기준 · 맡은 조직(팀장 보기, D2). 교대 편성은 근태 관리에. */
export const ATT_LAYOUT: readonly MenuLayoutGroup[] = [
  { label: '개인', keys: ['/erp/attendance', '/erp/attendance/me'] },
  {
    label: '근태 관리',
    keys: ['/erp/attendance/company', '/erp/attendance/anomalies', '/erp/attendance/wages', '/erp/attendance/shifts'],
  },
  {
    label: '연차',
    keys: [
      '/erp/attendance/hire-dates', '/erp/attendance/leave', '/erp/attendance/leave-policy',
      '/erp/attendance/leave-promotion', '/erp/attendance/leave-settlement', '/erp/attendance/substitute',
    ],
  },
  { label: '회사 기준', keys: ['/erp/attendance/work-schedules', '/erp/attendance/holidays', '/erp/attendance/codes'] },
  { label: '맡은 조직', keys: ['/erp/attendance/team'] },
];

/**
 * 인사(담당자 보기) — 내 인사(맨 위) · 맡은 조직 · 현황 · 직원 · 인사기록 · 조직 · 직급 · 계정 · 기준.
 * ★ 담당자 보기에만 쓴다 — 직원 보기는 `/erp/hr` 가 «내 인사 홈» 이라 같은 주소가 다른 묶음에 선다.
 *   직원 보기는 묶음이 셋뿐이라 오늘 그대로 둔다(시안도 담당자 보기만 그렸다).
 * ★ «사용자 관리» 를 2열에 세우는 것은 다음 판이다(D3).
 */
export const HR_ADMIN_LAYOUT: readonly MenuLayoutGroup[] = [
  { label: '내 인사', keys: ['/erp/hr/me/record', '/erp/hr/me/certificates'] },
  { label: '맡은 조직', keys: ['/erp/hr/me/team-hr'] },
  { label: '현황', keys: ['/erp/hr', '/erp/hr/stats'] },
  {
    label: '직원',
    keys: ['/erp/hr/employees', '/erp/hr/assignments', '/erp/hr/offboarding', '/erp/hr/certificates', '/erp/hr/profile-requests'],
  },
  {
    label: '인사기록',
    keys: ['/erp/hr/records/career', '/erp/hr/records/awards', '/erp/hr/evaluations', '/erp/hr/records/training'],
  },
  { label: '조직 · 직급', keys: ['/erp/hr/job-grades', '/erp/hr/job-roles', '/erp/hr/org-leaders'] },
  { label: '계정 · 기준', keys: ['/erp/hr/access-log', '/erp/hr/codes'] },
];

/**
 * 인사 담당자 보기의 «접기» — 지금 위치가 없는 묶음은 한 줄로 접는다(시안 hr_tree2 · 항목 19개).
 * ★ 지금 위치가 든 묶음 · 검색 중 · 손으로 편 묶음은 접지 않는다. 접힘은 기억하지 않는다
 *   (가이드 «기기에 남기던 접힘 상태»를 걷어낸 까닭과 같다 — 화면마다 다르게 열리면 안 된다).
 */
export function isFolded(groupLabel: string, opts: { enabled: boolean; query: string; activeGroup?: string; opened: ReadonlySet<string> }): boolean {
  if (!opts.enabled || opts.query.trim()) return false;
  // 메뉴에 없는 화면(입사 처리 · 사용자 관리 등)에 있으면 «지금 위치» 가 없다 — 그때는 다 펴 둔다(지도를 감추지 않는다)
  if (!opts.activeGroup || groupLabel === opts.activeGroup) return false;
  return !opts.opened.has(groupLabel);
}
